/*!
 * paramBus.js — Global Control-Signal Store & Pub/Sub
 * Part of oscillaScore control plane architecture
 * © 2025 Rob Canning — GPLv3
 *
 * The ParamBus is the central nervous system for control signals in Oscilla.
 * It stores the latest value of each named signal and notifies subscribers
 * when values change. This enables cross-cue modulation and external control.
 *
 * Signal Addressing:
 *   <uid>.<channel>        anything a cue publishes
 *   osc:<address>          an incoming OSC address, from follow(/addr, …)
 *
 * Examples:
 *   sliderA.t           - o2p traversal position
 *   orb1.angle          - rotation angle in degrees
 *   box1.sx             - scale X
 *   mic.amp             - audio analysis
 *   osc:pedal/1         - external OSC input
 *
 * The uid is the address; the kind of cue that publishes it is not part of the
 * path, so a follow() reads the same whatever drives it.
 */

// ===========================
// Module State
// ===========================

/** @type {Map<string, any>} Signal values by path */
const signals = new Map();

/** @type {Map<string, Set<Function>>} Subscribers by path */
const subscribers = new Map();

/** @type {Map<string, Set<Function>>} Wildcard subscribers (prefix matching) */
const wildcardSubscribers = new Map();

/** @type {boolean} Enable debug logging */
let debugMode = false;

// ===========================
// Core API
// ===========================

/**
 * Set a signal value and notify subscribers
 * @param {string} path - Signal path (e.g., "slider.t", "osc:pedal/1")
 * @param {any} value - Signal value
 * @param {Object} meta - Optional metadata (source, timestamp, etc.)
 * @returns {boolean} True if value changed
 */
export function set(path, value, meta = {}) {
  if (!path || typeof path !== 'string') {
    console.warn('[ParamBus] Invalid path:', path);
    return false;
  }

  const previous = signals.get(path);
  const changed = previous !== value;

  signals.set(path, value);

  if (debugMode && changed) {
    console.log(`[ParamBus] ${path} = ${formatValue(value)}`, meta);
  }

  // Notify direct subscribers
  const subs = subscribers.get(path);
  if (subs) {
    for (const callback of subs) {
      try {
        callback(value, path, meta);
      } catch (err) {
        console.error('[ParamBus] Subscriber error:', err);
      }
    }
  }

  // Notify wildcard subscribers
  for (const [prefix, wsubs] of wildcardSubscribers) {
    if (path.startsWith(prefix)) {
      for (const callback of wsubs) {
        try {
          callback(value, path, meta);
        } catch (err) {
          console.error('[ParamBus] Wildcard subscriber error:', err);
        }
      }
    }
  }

  return changed;
}

/**
 * Get a signal value
 * @param {string} path - Signal path
 * @param {any} fallback - Default value if not found
 * @returns {any} Signal value or fallback
 */
export function get(path, fallback = undefined) {
  if (signals.has(path)) {
    return signals.get(path);
  }
  return fallback;
}

/**
 * Subscribe to a signal path
 * @param {string} path - Signal path (use "*" suffix for wildcard, e.g., "o2p:*")
 * @param {Function} callback - Called with (value, path, meta)
 * @returns {Function} Unsubscribe function
 */
export function subscribe(path, callback) {
  if (!path || typeof callback !== 'function') {
    console.warn('[ParamBus] Invalid subscribe args:', path, callback);
    return () => {};
  }

  // Wildcard subscription (prefix matching)
  if (path.endsWith('*')) {
    const prefix = path.slice(0, -1);
    if (!wildcardSubscribers.has(prefix)) {
      wildcardSubscribers.set(prefix, new Set());
    }
    wildcardSubscribers.get(prefix).add(callback);

    return () => {
      const wsubs = wildcardSubscribers.get(prefix);
      if (wsubs) {
        wsubs.delete(callback);
        if (wsubs.size === 0) {
          wildcardSubscribers.delete(prefix);
        }
      }
    };
  }

  // Direct subscription
  if (!subscribers.has(path)) {
    subscribers.set(path, new Set());
  }
  subscribers.get(path).add(callback);

  return () => {
    const subs = subscribers.get(path);
    if (subs) {
      subs.delete(callback);
      if (subs.size === 0) {
        subscribers.delete(path);
      }
    }
  };
}


/** The paths someone follows that start with `prefix` (direct subscriptions). */
export function subscribedPaths(prefix) {
  return [...subscribers.keys()].filter(p => p.startsWith(prefix));
}

/**
 * Check if any signals matching a prefix have active subscribers.
 * Used by the visibility observer to avoid pausing animations that
 * are feeding a modulation target (e.g. o2p driving a synth param).
 * @param {string} prefix - Path prefix (e.g., "sliderA.")
 * @returns {boolean} True if at least one matching path has subscribers
 */
export function hasSubscribers(prefix) {
  // Check direct subscribers
  for (const path of subscribers.keys()) {
    if (path.startsWith(prefix)) return true;
  }
  // Check wildcard subscribers that would match this prefix; "*" (prefix "")
  // watches everything and modulates nothing, so it does not count
  for (const wcPrefix of wildcardSubscribers.keys()) {
    if (!wcPrefix) continue;
    if (prefix.startsWith(wcPrefix) || wcPrefix.startsWith(prefix)) return true;
  }
  return false;
}



/**
 * Get all signals as an object
 * @param {string} prefix - Optional prefix filter
 * @returns {Object} Signal path -> value mapping
 */
export function snapshot(prefix = '') {
  const result = {};
  for (const [path, value] of signals) {
    if (path.startsWith(prefix)) {
      result[path] = value;
    }
  }
  return result;
}





// ===========================
// Utility Functions
// ===========================

/**
 * Enable or disable debug logging
 * @param {boolean} enabled - Whether to enable debug mode
 */
export function setDebugMode(enabled) {
  debugMode = !!enabled;
}

/**
 * Format a value for logging
 * @param {any} value - Value to format
 * @returns {string} Formatted string
 */
export function formatValue(value) {
  if (typeof value === 'number') {
    return Number.isInteger(value) ? String(value) : value.toFixed(4);
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

// ===========================
// Window Binding (for debugging)
// ===========================

window.oscillaParamBus = {
  set,
  get,
  subscribe,
  hasSubscribers,
  snapshot,
  setDebugMode
};

// ===========================
// Default Export
// ===========================

export default {
  set,
  get,
  subscribe,
  hasSubscribers,
  snapshot,
  setDebugMode
};
